import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Loading } from '../../../../../utils/loading/loading';
import { EmptyData } from '../../../../../utils/empty-data/empty-data';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import { ScopeResolver, Tables } from '../../../../../shared/ui/shared-tables/tables/tables';
import { Pagination } from '../../../../../shared/ui/shared-tables/pagination/pagination';
import { RouteItem } from '../../../../../pages/admin-dashboard/admin-route-management/routes.model';
import { toRouteTableRows } from './route-table.mapper';
import { ActionInterface } from '../../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { SCOPES } from '../../../../../services/scope/scope-constants';

export type LoadState = 'loading' | 'success' | 'error';

@Component({
  selector: 'app-route-table',
  standalone: true,
  imports: [DatePipe, Loading, EmptyData, ErrorData, Tables, Pagination],
  templateUrl: './route-table.html',
  styleUrl: './route-table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RouteTable {
  private locale = inject(LOCALE_ID);

  routes = input<RouteItem[] | null>(null);
  state = input<LoadState>('loading');
  currentPage = input<number>(1);
  itemsPerPage = input<number>(25);
  totalItems = input<number>(0);
  totalPages = input<number>(1);
  isFiltersApplied = input<boolean>(false);

  pageChange = output<number>();
  pageSizeChange = output<number>();
  rowClick = output<number>();
  editRoute = output<number>();
  deleteRoute = output<number>();
  toggleActive = output<{ id: number; isActive: boolean }>();

  rows = computed(() => toRouteTableRows(this.routes() ?? [], this.isArabic()));

  activeDropdownId = signal<number | null>(null);
  currentActions = signal<ActionInterface[]>([]);

  tableHeaderData = signal([
    { key: 'index', label: $localize`:@@routeTableIndex:#` },
    { key: 'routeName', label: $localize`:@@routeTableName:Route Name` },
    { key: 'regionsCount', label: $localize`:@@routeTableRegions:Regions Count` },
    { key: 'pharmaciesCount', label: $localize`:@@routeTablePharmacies:Pharmacies Count` },
    { key: 'createdOnBy', label: $localize`:@@routeTableCreatedOn:Created On / By` },
    { key: 'lastModifiedOnBy', label: $localize`:@@routeTableLastModified:Last Modified On / By` },
    { key: 'status', label: $localize`:@@routeTableStatus:Status`, type: 'status' },
    { key: 'actions', label: '', type: 'actions' },
  ]);

  startItem = computed(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });

  endItem = computed(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  pageNumbers = computed(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const delta = 2;
    const range: number[] = [1];
    if (total <= 1) return range;
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) range.push(i);
    }
    range.push(total);
    const result: number[] = [];
    let prev: number | undefined;
    for (const i of range) {
      if (prev !== undefined) {
        if (i - prev === 2) result.push(prev + 1);
        else if (i - prev !== 1) result.push(-1);
      }
      result.push(i);
      prev = i;
    }
    return result;
  });

  isArabic() {
    return this.locale.startsWith('ar');
  }

  getActions(route: RouteItem): ActionInterface[] {
    return [
      { id: 1, titleEn: 'View Details', titleAr: 'عرض التفاصيل' },
      { id: 2, titleEn: 'Edit', titleAr: 'تعديل' },
      { id: 3, titleEn: 'Delete', titleAr: 'حذف' },
      {
        id: 4,
        titleEn: route.isActive ? 'Deactivate' : 'Activate',
        titleAr: route.isActive ? 'تعطيل' : 'تفعيل',
      },
    ];
  }

  toggleDropdown(id?: any) {
    console.log('TOGGLED!!!', id);

    if (this.activeDropdownId() === id) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(id);
      const route = this.routes()?.find((r) => r.id === id);
      console.log('route:', route);
      if (route) this.currentActions.set(this.getActions(route));
    }
  }

  handleAction(e: { event: Event; actionId: number; entity: any }) {
    const { event, actionId, entity } = e;
    const rowId = entity.id;
    console.log('action triggered!: ', e);
    event?.stopPropagation();
    const route = this.routes()?.find((r) => r.id === entity.id);
    if (!route) return;

    switch (actionId) {
      case 1:
        console.log('case 1');
        this.rowClick.emit(rowId);
        break;
      case 2:
        console.log('case 2');
        this.editRoute.emit(rowId);
        break;
      case 3:
        console.log('case 3');
        this.deleteRoute.emit(rowId);
        break;
      case 4:
        console.log('case 4');
        this.toggleActive.emit({ id: rowId, isActive: !route.isActive });
        break;
    }
  }

  onPreviousPage() {
    this.pageChange.emit(this.currentPage() - 1);
  }
  onNextPage() {
    this.pageChange.emit(this.currentPage() + 1);
  }
  onGoToPage(page: number) {
    this.pageChange.emit(page);
  }
  onChangePageNumber(size: number) {
    this.pageSizeChange.emit(Number(size));
  }
  onPageSizeChange() {}

  getScopeForAction: ScopeResolver = (action) =>
    SCOPES.ROUTES[action.scope as keyof typeof SCOPES.ROUTES] ?? SCOPES.ROUTES.READ_ALL;
}
